'use client';

import { Heading } from '@app/_components/ui/Heading/Heading';
import { Paragraph } from '@app/_components/ui/Paragraph/Paragraph';
import { Input } from '@components/inputs';
import { useAuth } from '@context/authContext';
import { Button } from '@ngg/components';
import { useSearchParams } from 'next/navigation';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { Dictionary } from 'types/common';

import {
    passwordMinLength,
    passwordRules,
    passwordValidationRegex,
} from '@/utils/passwordRegex';

import ToolTip from '../registration/forms/formPasswordStrengthToolTip';

const LoginAgain = ({ dictionary }: { dictionary: Dictionary }) => {
    const { auth } = useAuth();
    const { showError, loginWithEmail } = useAuth();

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const email = formData.get('email')?.toString();
        const password = formData.get('password')?.toString();
        if (!email) return showError('Missing email');
        if (!password) return showError('Missing password');
        return loginWithEmail(email, password);
    };

    return (
        <>
            <Heading className="text-lg font-bold">
                {dictionary.loginDialogSubheading}
            </Heading>
            <Paragraph className="mt-4 text-sm">
                {dictionary.newPasswordSaved}
            </Paragraph>
            <Paragraph className="mt-4 text-sm">
                {dictionary.resetPasswordLoginText}
            </Paragraph>

            <form
                onSubmit={handleSubmit}
                className="mt-6 grid grid-cols-1 gap-x-4 gap-y-3 md:mt-10 md:grid-cols-2">
                <Input
                    type="email"
                    name="email"
                    id="email"
                    required
                    label={dictionary?.formEmailLabel}
                />

                <Input
                    type="password"
                    name="password"
                    id="password"
                    required
                    label={dictionary?.formPasswordLabel}
                />

                <Button
                    type="submit"
                    isLoading={auth?.loading}
                    className="btn-primary col-span-full mx-auto mt-3 flex w-full sm:max-w-[356px]">
                    {dictionary.login}
                </Button>

                {auth?.error ? (
                    <p className="mt-2 text-sm text-error">
                        {dictionary.errorLoginText}
                    </p>
                ) : null}
            </form>
        </>
    );
};

export default function ResetPasswordView({
    dictionary,
    children,
}: {
    dictionary: Dictionary;
    children: ReactNode;
}) {
    const { auth } = useAuth();
    const searchParams = useSearchParams();
    const verificationCode = searchParams.get('oobCode') || '';
    const { verifyCode, verifyPassword, showReset } = useAuth();

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [state, setState] = useState<'idle' | 'reset' | 'error' | 'login'>(
        'idle',
    );
    const [validationError, setValidationError] = useState({
        password: '',
        repeatPassword: '',
    });

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const password = formData.get('password')?.toString();
        const repeatPassword = formData.get('repeatPassword')?.toString();

        if (password && repeatPassword) {
            if (password !== repeatPassword) {
                setValidationError((prev) => ({
                    ...prev,
                    repeatPassword: 'errorPasswordsDoNotMatch',
                }));
                return;
            }
            if (password && !passwordValidationRegex.test(password)) {
                setValidationError((prev) => ({
                    ...prev,
                    password: 'errorPasswordTooWeak',
                }));
                return;
            }

            try {
                await verifyPassword(verificationCode, password);
                setState('login');
            } catch (e) {
                console.warn('Error in ResetPasswordView', e);
            }
        }
    };

    useEffect(() => {
        if (state === 'idle') {
            if (!verificationCode) {
                setState('error');
            } else {
                verifyCode(verificationCode)
                    .then(() => setState('reset'))
                    .catch(() => setState('error'));
            }
        }
    }, [state, verificationCode, verifyCode]);

    if (state === 'idle') return null;

    if (state === 'error') {
        return (
            <div className="col-span-full mx-auto mt-3 w-fit space-y-3">
                <p className="text-error">
                    {dictionary.errorPasswordResetTokenInvalid}
                </p>
                <button
                    type="button"
                    className="mx-auto block underline"
                    onClick={() => showReset()}>
                    {dictionary.resetPasswordBtn}
                </button>
            </div>
        );
    }

    if (state === 'login') {
        return <LoginAgain dictionary={dictionary} />;
    }

    if (state === 'reset') {
        return (
            <>
                {children}
                <form
                    noValidate
                    onSubmit={handleSubmit}
                    className="mt-6 grid grid-cols-1 gap-x-4 gap-y-3 md:mt-10 md:grid-cols-2">
                    <Input
                        name="password"
                        id="password"
                        type="password"
                        label={dictionary?.formPasswordLabel}
                        hint="."
                        required
                        aria-invalid={!!validationError.password}
                        HintComponent={() =>
                            ToolTip({
                                open: isTooltipOpen,
                                setOpen: setIsTooltipOpen,
                                dictionary,
                            })
                        }
                        autoComplete="new-password"
                        minLength={passwordMinLength}
                        passwordrules={passwordRules}
                    />
                    <Input
                        name="repeatPassword"
                        id="repeatPassword"
                        type="password"
                        label={dictionary?.formRepeatPasswordLabel}
                        aria-invalid={
                            !!validationError.repeatPassword ||
                            !!validationError.password
                        }
                        required
                        hint={
                            dictionary?.[
                                validationError.repeatPassword ||
                                    validationError.password
                            ]
                        }
                        autoComplete="new-password"
                        minLength={passwordMinLength}
                        passwordrules={passwordRules}
                    />

                    <p className="col-span-full text-xs text-grey-300">
                        {dictionary.passwordHint}
                    </p>

                    <Button
                        type="submit"
                        isLoading={auth?.loading}
                        className="btn-primary col-span-full mx-auto mt-3 flex w-full sm:max-w-[356px]">
                        {dictionary.formConfirmNewPasswordButton}
                    </Button>
                </form>
            </>
        );
    }
}
